import { useState, useEffect } from "react";
import { Plus, X, Building2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText } from "@/lib/api";

export default function AdminAgencies() {
  const [agencies, setAgencies] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", owner_phone: "", commission_pct: 10 });
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/admin/agencies").then(({ data }) => setAgencies(data.agencies)).catch(() => setAgencies([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/admin/agencies", { ...form, commission_pct: Number(form.commission_pct) });
      toast.success("Agency created — share the invite code with streamers!");
      setShowForm(false);
      setForm({ name: "", owner_phone: "", commission_pct: 10 });
      load();
    } catch (err) {
      toast.error(errText(err));
    }
    setBusy(false);
  };

  const updatePct = async (ag, pct) => {
    if (pct === "" || Number(pct) === ag.commission_pct) return;
    try {
      await adminApi.put(`/admin/agencies/${ag.id}`, { commission_pct: Number(pct) });
      toast.success("Commission updated");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const toggleStatus = async (ag) => {
    try {
      await adminApi.put(`/admin/agencies/${ag.id}`, { status: ag.status === "active" ? "suspended" : "active" });
      toast.success(ag.status === "active" ? "Agency suspended — commissions paused" : "Agency re-activated");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (ag) => {
    if (!window.confirm(`Dissolve "${ag.name}"? All ${ag.members_count} members will be released.`)) return;
    try {
      await adminApi.delete(`/admin/agencies/${ag.id}`);
      toast.success("Agency dissolved");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div data-testid="admin-agencies">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-extrabold font-display">Agencies</h1>
        <button data-testid="new-agency-button" onClick={() => setShowForm(true)}
          className="gradient-primary text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-1.5"><Plus size={14} /> New Agency</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <form onSubmit={create} className="glass bg-[#14141c] rounded-3xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-bold font-display flex items-center gap-2"><Building2 size={16} className="text-pink-400" /> Create Agency</h2>
              <button type="button" data-testid="agency-form-close" onClick={() => setShowForm(false)}><X size={18} className="text-zinc-400" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="agency-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Agency name (e.g. Star Talents)"
                className="w-full bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
              <input data-testid="agency-owner-phone-input" required value={form.owner_phone} onChange={(e) => setForm((f) => ({ ...f, owner_phone: e.target.value.replace(/\D/g, "") }))}
                placeholder="Owner's phone number (must be a registered user)" maxLength={10}
                className="w-full bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
              <div>
                <label className="text-[10px] text-zinc-500 font-semibold">Commission % (of members' ruby earnings, platform-funded)</label>
                <input data-testid="agency-commission-input" type="number" min={0} max={50} required value={form.commission_pct}
                  onChange={(e) => setForm((f) => ({ ...f, commission_pct: e.target.value }))}
                  className="w-full bg-[#1A1A24] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500 mt-1" />
              </div>
              <button data-testid="agency-create-submit" disabled={busy} className="w-full gradient-primary text-white font-bold py-3 rounded-xl disabled:opacity-40">
                {busy ? "Creating…" : "Create Agency"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-zinc-500 text-left border-b border-white/10">
              <th className="p-3">Agency</th><th>Owner</th><th>Invite Code</th><th>Commission %</th><th>Members</th><th>Total ♦️</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {agencies?.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-zinc-600">No agencies yet — create the first one!</td></tr>
            )}
            {agencies?.map((ag) => (
              <tr key={ag.id} className="border-b border-white/5" data-testid={`agency-row-${ag.id}`}>
                <td className="p-3 text-white font-semibold">{ag.name}</td>
                <td className="text-zinc-400">{ag.owner?.name}</td>
                <td><span className="font-mono font-bold tracking-widest text-pink-400">{ag.invite_code}</span></td>
                <td>
                  <input type="number" min={0} max={50} defaultValue={ag.commission_pct} data-testid={`agency-pct-${ag.id}`}
                    onBlur={(e) => updatePct(ag, e.target.value)}
                    className="w-16 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-1 text-emerald-400 font-bold outline-none focus:border-violet-500" />
                </td>
                <td className="text-zinc-300 font-semibold">{ag.members_count}</td>
                <td className="text-cyan-300 font-semibold">{ag.total_commission?.toLocaleString()}</td>
                <td>
                  <button data-testid={`agency-status-${ag.id}`} onClick={() => toggleStatus(ag)}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${ag.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>
                    {ag.status}
                  </button>
                </td>
                <td className="pr-3">
                  <button data-testid={`agency-delete-${ag.id}`} onClick={() => remove(ag)}
                    className="p-1.5 rounded-lg bg-white/5 text-zinc-500 hover:text-red-400"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
